import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Client as NesClient } from '@hapi/nes/lib/client';
import moment from 'moment';
import store from 'store2';
import config from '../config';

const RefreshJobContext = createContext(null);

const wsUrl = config.apiHost.replace(/^http/, 'ws');

const JOB_TIMEOUT_MS = 15 * 60 * 1000;
const PING_INTERVAL_MS = 15 * 1000;

const STATUS = {
    IDLE: 'idle',
    CONNECTING: 'connecting',
    RUNNING: 'running',
    COMPLETE: 'complete',
    ERROR: 'error',
};

// Health of the WebSocket itself while a job runs (separate from the job status)
const CONNECTION = {
    LIVE: 'live',
    RECONNECTING: 'reconnecting',
    LOST: 'lost',
};

export const RefreshJobProvider = ({ userId, children }) => {
    const [status, setStatus] = useState(STATUS.IDLE);
    const [message, setMessage] = useState('');
    const [startedAt, setStartedAt] = useState(null);
    const [finishedAt, setFinishedAt] = useState(null);
    const [connection, setConnection] = useState(null);
    const [lastAliveAt, setLastAliveAt] = useState(null);
    const [latencyMs, setLatencyMs] = useState(null);
    const [pod, setPod] = useState(null);
    const clientRef = useRef(null);
    const autoCloseRef = useRef(null);
    const pingRef = useRef(null);
    const interruptedRef = useRef(false);

    const dismiss = useCallback(() => {
        setStatus(STATUS.IDLE);
        setMessage('');
    }, []);

    const disconnect = useCallback(() => {
        if (autoCloseRef.current) {
            clearTimeout(autoCloseRef.current);
            autoCloseRef.current = null;
        }
        if (pingRef.current) {
            clearInterval(pingRef.current);
            pingRef.current = null;
        }
        const client = clientRef.current;
        // Clear the ref first so the client's onDisconnect handler ignores this intentional close
        clientRef.current = null;
        if (client) {
            try { client.disconnect(); } catch (_) {}
        }
        setConnection(null);
    }, []);

    // Round-trip request over the socket — proves the pod holding the socket is answering
    const ping = useCallback(async (client) => {
        const sentAt = Date.now();
        try {
            const { payload } = await client.request({ method: 'GET', path: '/refresh/ping' });
            if (clientRef.current !== client) return;
            setLastAliveAt(Date.now());
            setLatencyMs(Date.now() - sentAt);
            setPod(payload && payload.pod);
        } catch (_) {
            // A failed ping means the socket dropped; onDisconnect reports that
        }
    }, []);

    const startRefresh = useCallback(async (params, type) => {
        if (!userId) return;

        disconnect();

        setStatus(STATUS.CONNECTING);
        setMessage('');
        setStartedAt(Date.now());
        setFinishedAt(null);
        setLastAliveAt(null);
        setLatencyMs(null);
        setPod(null);
        interruptedRef.current = false;

        const token = store('authToken');
        const client = new NesClient(wsUrl);
        clientRef.current = client;

        client.onDisconnect = (willReconnect) => {
            if (clientRef.current !== client) return;
            interruptedRef.current = true;
            setConnection(willReconnect ? CONNECTION.RECONNECTING : CONNECTION.LOST);
        };
        client.onConnect = () => {
            if (clientRef.current !== client) return;
            setConnection(CONNECTION.LIVE);
            setLastAliveAt(Date.now());
        };

        try {
            await client.connect({ auth: { headers: { authorization: `bearer ${token}` } } });

            await client.subscribe(`/refresh/${userId}`, (update) => {
                if (update.status === 'complete') {
                    setStatus(STATUS.COMPLETE);
                    setMessage('Report refresh complete — data is now up to date.');
                } else if (update.status === 'error') {
                    setStatus(STATUS.ERROR);
                    setMessage(update.message || 'Refresh failed.');
                }
                disconnect();
            });
        } catch (err) {
            console.error('[RefreshJob] WebSocket connect/subscribe failed:', { wsUrl, userId, type: err && err.type, statusCode: err && err.statusCode }, err);
            setStatus(STATUS.ERROR);
            setMessage(`Could not connect to the notification service${err && err.message ? `: ${err.message}` : '.'}`);
            disconnect();
            return;
        }

        // Trigger the job over the same socket, so it runs on the pod that holds our subscription.
        // An HTTP POST could be load-balanced to a different pod, whose publish would never reach us.
        try {
            await client.request({
                method: 'POST',
                path: `/payReports/triggerRefresh/${encodeURIComponent(params || '')}?type=${encodeURIComponent(type || '')}`,
                // Authorization is not allowed here — the socket's own login is used instead
                headers: { 'x-user-date-time': moment().format('Y-MM-DD') },
            });
        } catch (err) {
            console.error('[RefreshJob] trigger over WebSocket failed:', { statusCode: err && err.statusCode }, err);
            setStatus(STATUS.ERROR);
            setMessage(`Failed to start refresh${err && err.message ? `: ${err.message}` : '.'}`);
            disconnect();
            return;
        }

        setStatus(STATUS.RUNNING);
        ping(client);
        pingRef.current = setInterval(() => ping(client), PING_INTERVAL_MS);

        autoCloseRef.current = setTimeout(() => {
            if (clientRef.current !== client) return;
            setStatus(STATUS.ERROR);
            setMessage(interruptedRef.current
                ? 'Connection was interrupted during the refresh, so the result may have been missed — reload the report to check.'
                : 'Refresh timed out — no response after 15 minutes.');
            disconnect();
        }, JOB_TIMEOUT_MS);
    }, [userId, disconnect, ping]);

    // Freeze the elapsed time when the job settles
    useEffect(() => {
        if (status === STATUS.COMPLETE || status === STATUS.ERROR) setFinishedAt(Date.now());
    }, [status]);

    useEffect(() => () => disconnect(), [disconnect]);

    return (
        <RefreshJobContext.Provider value={{ status, message, startedAt, finishedAt, connection, lastAliveAt, latencyMs, pod, startRefresh, dismiss }}>
            {children}
        </RefreshJobContext.Provider>
    );
};

export const useRefreshJob = () => useContext(RefreshJobContext);
export { STATUS as REFRESH_STATUS, CONNECTION as REFRESH_CONNECTION };
